import { formatLength as coreFormatLength } from '@stageplottter/core';
import { uiLocale } from './locale';
/** Display conversion. Data is always stored in SI (metres, decision Q13). */
export type Units = 'metric' | 'imperial';
export type UnitsPreference = 'auto' | Units;

export const METRES_PER_FOOT = 0.3048;
export const METRES_PER_INCH = 0.0254;

export function resolveUnits(pref: UnitsPreference, boardUnits: Units): Units {
  return pref === 'auto' ? boardUnits : pref;
}

/** Rounds to `digits` decimals without floating point noise. */
export function round(value: number, digits = 2): number {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
}

/** 2.5 → "2.50 m"; imperial: 2.5 → `8′ 2.4″`. */
export function formatLength(metres: number, units: Units): string {
  if (units === 'metric') {
    const text = round(metres, 2).toFixed(2);
    // German UI: decimal comma (OIL-157)
    return `${uiLocale() === 'de' ? text.replace('.', ',') : text} m`;
  }
  const sign = metres < 0 ? '-' : '';
  const totalInches = round(Math.abs(metres) / METRES_PER_INCH, 1);
  let feet = Math.floor(totalInches / 12);
  let inches = round(totalInches - feet * 12, 1);
  if (inches >= 12) {
    feet += 1;
    inches = 0;
  }
  return `${sign}${feet}′ ${inches}″`;
}

/** A number with up to `digits` decimals, without trailing zeros; decimal comma in German (OIL-200). */
export function localNumber(value: number, digits = 2): string {
  const text = String(round(value, digits));
  return uiLocale() === 'de' ? text.replace('.', ',') : text;
}

/** Compact length for tables and titles: "0,9 m" / "0.9 m"; imperial `6'7"` (OIL-200). */
export function formatLengthCompact(metres: number, units: Units): string {
  return units === 'metric' ? `${localNumber(metres)} m` : coreFormatLength(metres, units);
}

/** Number shown in an editable field (metres or feet, decimal). */
export function toDisplayNumber(metres: number, units: Units): number {
  return units === 'metric' ? round(metres, 3) : round(metres / METRES_PER_FOOT, 3);
}

/**
 * Parses user input into metres. Accepts plain numbers (in the display unit), "2.5m", "250cm",
 * "8ft", `8'`, `8' 2"`, `14"`, "14in". Comma decimal separators are accepted. Returns null for invalid input.
 */
export function parseLength(input: string, units: Units): number | null {
  const s = input.trim().replace(',', '.').toLowerCase();
  if (s === '') return null;
  const num = '(-?\\d+(?:\\.\\d+)?|-?\\.\\d+)';
  const plain = new RegExp(`^${num}$`).exec(s);
  if (plain) {
    const v = Number(plain[1]);
    return units === 'metric' ? v : v * METRES_PER_FOOT;
  }
  const withUnit = new RegExp(`^${num}\\s*(mm|cm|m|ft|in)$`).exec(s);
  if (withUnit) {
    const v = Number(withUnit[1]);
    const factor: Record<string, number> = {
      mm: 0.001,
      cm: 0.01,
      m: 1,
      ft: METRES_PER_FOOT,
      in: METRES_PER_INCH,
    };
    return v * factor[withUnit[2]!]!;
  }
  const feetInches = new RegExp(`^${num}\\s*['′]\\s*(?:${num}\\s*(?:"|″|''))?$`).exec(s);
  if (feetInches) {
    const ft = Number(feetInches[1]);
    const inch = feetInches[2] === undefined ? 0 : Number(feetInches[2]);
    return ft * METRES_PER_FOOT + Math.sign(ft || 1) * inch * METRES_PER_INCH;
  }
  const inchesOnly = new RegExp(`^${num}\\s*(?:"|″)$`).exec(s);
  if (inchesOnly) return Number(inchesOnly[1]) * METRES_PER_INCH;
  return null;
}

/** Unit suffix for editable fields. */
export function unitSuffix(units: Units): string {
  return units === 'metric' ? 'm' : 'ft';
}
